"use client"

import React, { useEffect, useState } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { useTheme } from "next-themes"
import { PanelLeft } from "lucide-react"
import { GlobalSidebar, getCurrentAppPage, readRailCollapsed, writeRailCollapsed } from "../lib/global-sidebar-nav"
import { AppBreadcrumb } from "../ui/breadcrumb"
import { WorldClock } from "./world-clock"
import { MarketClock } from "./market-clock"
import { LiveNews } from "./live-news"
import { NotificationBell, type NotificationItem } from "./notification-bell"
import { StatusPulse } from "./status-pulse"
import { ThemeToggle } from "./theme-toggle"
import { OutageBanner } from "./outage-banner"
import type { SearchItem } from "./search-dialog"
import { useSidebarUser } from "../lib/auth"
import { useIsMobile } from "../hooks/use-mobile"
import type { BreadcrumbConfig } from "./header"

export type { BreadcrumbConfig } from "./header"

export interface UserData {
  name: string
  email: string
  avatar: string
}

// Sidebar widths — keep in sync with GlobalSidebar (aside = 268, rail = 60) so
// the header's logo column lines up with the sidebar's right border.
const SB_EXPANDED = 268
const SB_RAIL = 60

export interface AppShellProps {
  children: React.ReactNode
  /** Optional breadcrumb override; the last entry's label becomes the page
   *  title shown after the app name (e.g. a document name). When omitted the
   *  breadcrumb auto-derives from the current host + path. */
  breadcrumbs?: BreadcrumbConfig[]
  /** Document name shown as the trailing breadcrumb (overrides page title). */
  documentName?: string
  /** Returns the auth token for the notification bell / world clock. */
  getAuthToken?: () => string | null
  notificationsApiBaseUrl?: string
  notificationsHref?: string
  logoHref?: string
  /** Override the resolved user (else taken from the shared auth cookie). */
  user?: UserData
  /** Force the active app host (rarely needed; auto-detected from origin). */
  activeHost?: string
  /** Full-bleed content: the content area is `overflow-hidden` + full height
   *  instead of a scroll container — for apps that manage their own layout/
   *  scrolling (e.g. the webIDE, maps). */
  fullBleed?: boolean
  /** Apply the `.askfinzdesign` brand scope (Ink-on-Paper) to the content area
   *  (default true). Set false for apps that run their own scoped design
   *  system (news `.afn`, shop `.mkt`) to avoid double-skinning. The header,
   *  sidebar, footer and chat-drawer are outside this scope and never change. */
  brand?: boolean

  // ── Legacy props (accepted but no longer used; the unified GlobalSidebar
  //    builds its own nav + search). Kept optional so existing callers compile
  //    during the migration; safe to drop from call sites. ──
  navMain?: unknown
  navSecondary?: unknown
  showSearch?: boolean
  searchPlaceholder?: string
  searchItems?: SearchItem[]
  sidebarToggleDistance?: string
  onSearch?: (query: string) => Promise<SearchItem[]> | SearchItem[]
  actions?: React.ReactNode
  /** show the cycling major-exchange market clock before the world clock (finance) */
  showMarketClock?: boolean
  /** client-side notifications merged into the header bell (e.g. finance alerts) */
  notificationItems?: NotificationItem[]
}

/**
 * Unified application shell for every askFinz web app (except login):
 * the redesigned GlobalSidebar + a header (logo, sidebar toggle, breadcrumb,
 * world clock, live news, notifications, status, theme). The Footer and the
 * global ChatDrawer come from ClientLayout in the root layout.
 */
export function AppShell({
  children,
  breadcrumbs,
  documentName,
  getAuthToken,
  notificationsApiBaseUrl,
  notificationsHref,
  logoHref = "/",
  user: userProp,
  activeHost,
  fullBleed,
  brand = true,
  showMarketClock = false,
  notificationItems,
}: AppShellProps) {
  const pathname = usePathname() ?? "/"
  const { theme, systemTheme } = useTheme()
  const hookUser = useSidebarUser()
  const user = userProp || hookUser
  const isMobile = useIsMobile()

  const [mounted, setMounted] = useState(false)
  const [origin, setOrigin] = useState("")
  const [railCollapsed, setRailCollapsed] = useState(false)

  useEffect(() => {
    setMounted(true)
    setOrigin(window.location.origin)
    setRailCollapsed(readRailCollapsed())
    const onToggle = () => setRailCollapsed((v) => { const n = !v; writeRailCollapsed(n); return n })
    window.addEventListener("afn-nav:toggle-rail", onToggle)
    return () => window.removeEventListener("afn-nav:toggle-rail", onToggle)
  }, [])

  const dark = (theme === "system" ? systemTheme : theme) === "dark"
  // On mobile the sidebar is an off-canvas drawer, so the header logo column no
  // longer needs to mirror the 268px sidebar width — show the compact symbol in
  // a narrow box and let the breadcrumb + actions have the rest of the row.
  const useSymbol = isMobile || railCollapsed
  const logo = useSymbol
    ? (dark ? "/shared/logos/header/AskFinz_symbol_light.svg" : "/shared/logos/header/AskFinz_symbol_black.svg")
    : (dark ? "/shared/logos/header/askFinz_white.svg" : "/shared/logos/header/askFinz_black.svg")
  const logoBoxW = isMobile ? 56 : (railCollapsed ? SB_RAIL : SB_EXPANDED)

  const cur = getCurrentAppPage(origin, pathname)
  const overrideTitle = documentName || (breadcrumbs && breadcrumbs.length ? breadcrumbs[breadcrumbs.length - 1].label : undefined)

  return (
    <>
      <OutageBanner />

      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-[100] w-full">
        <div
          data-header
          className="flex h-16 items-stretch border-b backdrop-blur supports-[backdrop-filter]:backdrop-blur w-full"
          style={{ backgroundColor: "hsl(var(--header-footer-background))" }}
        >
          <Link
            href={logoHref}
            className="flex items-center"
            style={{
              width: logoBoxW, flex: "none", boxSizing: "border-box",
              borderRight: "1px solid transparent",
              padding: useSymbol ? "0" : "0 16px",
              justifyContent: useSymbol ? "center" : "flex-start",
              transition: "width .35s cubic-bezier(0.4,0,0.2,1)",
            }}
          >
            {mounted && (
              <img
                src={logo}
                alt="AskFinz"
                className="h-5 sm:h-6 w-auto object-contain max-w-[70px]"
                onError={(e) => {
                  const t = e.currentTarget as HTMLImageElement
                  const fb = dark ? "/shared/logos/header/askFinz_white.svg" : "/shared/logos/header/askFinz_black.svg"
                  if (t.src !== fb) t.src = fb
                }}
              />
            )}
          </Link>

          <div className="flex items-center" style={{ flex: 1, minWidth: 0, gap: 10, padding: "0 16px" }}>
            <button
              onClick={() => { if (typeof window !== "undefined") window.dispatchEvent(new Event("afn-nav:toggle-rail")) }}
              title={railCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              className="flex items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors shrink-0"
              style={{ width: 30, height: 30 }}
            >
              <PanelLeft size={16} />
            </button>

            {/* Breadcrumb is hidden on mobile — the sidebar drawer handles nav,
                and hiding it frees the full row for the action buttons. */}
            {!isMobile && (
              <AppBreadcrumb host={cur.host} appName={cur.appName} pageTitle={overrideTitle ?? cur.pageTitle} />
            )}

            <div style={{ flex: 1 }} />

            <div className="flex items-center gap-1 sm:gap-1.5 flex-shrink-0">
              {/* Cycling major-exchange market clock (finance), then the world clock. */}
              {showMarketClock && !isMobile && <MarketClock />}
              {/* World clock + live news are space-hungry; drop them on phones so
                  the essential actions (notifications, status, theme) always fit. */}
              {!isMobile && <WorldClock getAuthToken={getAuthToken} />}
              {!isMobile && <LiveNews />}
              <NotificationBell getAuthToken={getAuthToken} apiBaseUrl={notificationsApiBaseUrl} notificationsHref={notificationsHref} localItems={notificationItems} />
              <StatusPulse />
              <ThemeToggle />
            </div>
          </div>
        </div>
      </div>

      {/* Content: sidebar between the fixed header and the ClientLayout footer.
          Use 100dvh (dynamic viewport height) so the wrapper always fills the
          visible viewport on mobile — the footer is position:fixed so it never
          affects flow, and the page content already has pb-[--footer-height]
          to keep the last item above the fixed footer. */}
      <div className="flex flex-col w-full" style={{ height: "100dvh", paddingTop: "4rem", paddingBottom: "var(--footer-height, 2.8125rem)" }}>
        <div className="flex-1 min-h-0 overflow-hidden relative flex" style={{ zIndex: 0 }}>
          <GlobalSidebar activeHost={activeHost} />
          <div className={(brand ? "askfinzdesign " : "") + "min-h-0 h-full " + (fullBleed ? "overflow-hidden" : "overflow-y-auto")} style={{ flex: 1 }}>
            {children}
          </div>
        </div>
      </div>
    </>
  )
}
